import { Link, NavLink } from 'react-router-dom';
import { ArrowLeft, Building2, Layers, MessageSquare, PanelsTopLeft } from 'lucide-react';
import Headway from '../components/Headway';
import MenuHeader from './MenuHeader';
import NotificationCenter from '../components/Notification/NotificationCenter';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../hooks';
import HeaderTitle from './HeaderTitle';
import { isAdmin } from 'components/AdminOnly';
import RapportContainer from './RapportContainer';
import AuditsContainer from './AuditsContainer';
import { Button, buttonVariants } from 'components/ui/button';
import { cn } from 'lib/utils';

interface IProps {
  backLink?: string;
  backText?: string;
}

export default function HeaderBar({ backLink, backText }: IProps) {
  const hasGroups = useAppSelector((state) => state.groups.groups.length > 0);
  const uid = useAppSelector((state) => state.user.details.uid);
  const orgTitle = useAppSelector((state) => state.orgs.currentOrg?.title);
  const { t } = useTranslation();
  const linkClass = cn(
    buttonVariants({ variant: 'ghost', size: 'sm' }),
    '!text-muted-foreground hover:!text-foreground'
  );
  return (
    <header className="border-b bg-background px-4 py-3 md:px-6">
      <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-3">
        <div className="flex min-w-0 flex-1 items-center gap-3 lg:max-w-xs">
          {backLink ? (
            <Button asChild variant="ghost" size="icon" className="shrink-0">
              <Link to={backLink} aria-label={backText || t('Tilbage')}>
                <ArrowLeft aria-hidden="true" />
              </Link>
            </Button>
          ) : (
            <PanelsTopLeft aria-hidden="true" className="h-5 w-5 shrink-0 text-muted-foreground" />
          )}
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-foreground">
              <HeaderTitle backText={backText} />
            </div>
            {backText && orgTitle && (
              <div title={orgTitle} className="truncate text-xs text-muted-foreground">
                {orgTitle}
              </div>
            )}
          </div>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-1 lg:order-3">
          <NotificationCenter />
          <Headway />
          <div className="mx-1 h-5 border-l" aria-hidden="true" />
          <MenuHeader />
        </div>
        <nav
          aria-label={t('Hovednavigation')}
          className="order-last flex w-full flex-wrap items-center gap-1 border-t pt-2 lg:order-2 lg:w-auto lg:border-0 lg:pt-0"
        >
          <NavLink
            to="/depts"
            className={linkClass}
            activeClassName="!bg-secondary !text-foreground"
          >
            <Building2 aria-hidden="true" />
            {t('header.depts')}
          </NavLink>
          {(hasGroups || isAdmin(uid)) && (
            <NavLink
              to="/groups"
              className={linkClass}
              activeClassName="!bg-secondary !text-foreground"
            >
              <Layers aria-hidden="true" />
              {t('header.groups')}
            </NavLink>
          )}
          <NavLink
            to="/issues"
            className={linkClass}
            activeClassName="!bg-secondary !text-foreground"
          >
            <MessageSquare aria-hidden="true" />
            {t('header.issuesOverview')}
          </NavLink>
          <AuditsContainer />
          <RapportContainer buttonType="link" />
        </nav>
      </div>
    </header>
  );
}
